import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TotalUsersLineGraph = ({
  isLoading,
  data,
  selectedMonthYear,
  setSelectedMonthYear,
}: any) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Generate options for the past 24 months
  const dropdownOptions = Array.from({ length: 24 }, (_, i) => {
    const date = new Date(currentYear, currentMonth - i, 1);
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    const label = `${monthNames[date.getMonth()]} ${y}`;
    const value = `${m}-${y}`;
    return { label, value };
  });

  const growth = data?.growthOverTime;
  const xLen = Array.isArray(growth?.x) ? growth.x.length : 0;
  const flexLen = Array.isArray(growth?.flexible) ? growth.flexible.length : 0;
  const outLen = Array.isArray(growth?.outright) ? growth.outright.length : 0;
  const hasBackendGrowth = xLen > 0 && (flexLen === xLen || outLen === xLen);
  const padSeries = (arr: unknown, len: number): number[] => {
    if (!Array.isArray(arr) || arr.length !== len) return Array.from({ length: len }, () => 0);
    return arr.map((v) => Number(v)).map((n) => (Number.isFinite(n) ? n : 0));
  };

  const barData = {
    labels: hasBackendGrowth ? growth.x : [],
    datasets: [
      {
        label: "Flexible",
        data: hasBackendGrowth ? padSeries(growth.flexible, xLen) : [],
        backgroundColor: "#008B50",
        borderRadius: 4,
        maxBarThickness: 12,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      {
        label: "Outright",
        data: hasBackendGrowth ? padSeries(growth.outright, xLen) : [],
        backgroundColor: "#FF9500", // Using orange for contrast like the other bar chart
        borderRadius: 4,
        maxBarThickness: 12,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  };

  const barOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll build a custom legend
      },
      tooltip: {
        backgroundColor: "#1f2937",
        titleFont: { size: 12 },
        bodyFont: { size: 13, weight: "bold" },
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#9CA3AF" }
      },
      y: {
        grid: { color: "#f3f4f6", borderDash: [4, 4] },
        ticks: { font: { size: 11 }, color: "#9CA3AF" },
        beginAtZero: true
      },
    },
  };

  return (
    <section className="bg-white shadow-md rounded-xl p-5 mb-10">
      <section className="flex items-center justify-between mb-6">
        <article className="flex items-center gap-6">
          <h2 className="text-xl sm:text-2xl font-bold font-nunito text-gray-800">Total Users Growth</h2>
          <div className="hidden sm:flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#008B50]"></span>
              <span className="text-xs font-medium text-gray-600">Flexible</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF9500]"></span>
              <span className="text-xs font-medium text-gray-600">Outright</span>
            </div>
          </div>
        </article>
        <section className="flex items-center">
          <div className="relative">
            <select
              className="appearance-none bg-gray-100 text-gray-700 text-sm font-semibold pl-4 pr-10 py-2 rounded-lg cursor-pointer outline-none hover:bg-gray-200 focus:ring-2 focus:ring-[#008B50]/20 transition-all border border-transparent hover:border-gray-300"
              value={selectedMonthYear}
              onChange={(e) => setSelectedMonthYear(e.target.value)}
              disabled={isLoading}
            >
              {dropdownOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
            </div>
          </div>
        </section>
      </section>
      <section className="h-[280px] mt-4">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
            Loading user growth…
          </div>
        ) : !hasBackendGrowth ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
            No user growth data for this period.
          </div>
        ) : (
          <Bar data={barData} options={barOptions} />
        )}
      </section>
    </section>
  );
};

export default TotalUsersLineGraph;
